import { inject, Injectable, signal } from '@angular/core';
import { ErrorService, indicateSignal } from '@pgaoc/shared-lib';
import { ActivatedRoute } from '@angular/router';
import { EMPTY, map, switchMap } from 'rxjs';
import { GolfIndustryHoursRepository } from '../../../core/repository/golf-industry-hours-repository';
import { GolfIndustryHours } from '../../../core/models/golf-industry-hours/golf-industry-hours';

@Injectable()
export class ReviewGolfIndustryHoursViewModel {
  private readonly golfIndustryHoursRepository = inject(GolfIndustryHoursRepository);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly errorService = inject(ErrorService);

  private readonly golfIndustryHoursGuid$ = this.activatedRoute.params.pipe(
    map((params): string | undefined => params?.golfIndustryHoursGuid),
  );
  readonly isGolfIndustryHoursLoading = signal<boolean>(false);
  readonly golfIndustryHours = signal<GolfIndustryHours>({});

  readonly isStatusUpdateSubmitting = signal<boolean>(false);

  constructor() {
    this.golfIndustryHoursGuid$
      .pipe(
        switchMap(golfIndustryHoursGuid =>
          golfIndustryHoursGuid
            ? this.golfIndustryHoursRepository
                .unauthenticatedGetGolfIndustryHours(golfIndustryHoursGuid)
                .pipe(indicateSignal(this.isGolfIndustryHoursLoading))
            : EMPTY,
        ),
      )
      .subscribe({
        next: golfIndustryHours => {
          if (!golfIndustryHours) return;
          this.golfIndustryHours.set(golfIndustryHours);
        },
        error: e => this.errorService.handle(e, $localize`Error Retrieving Golf Industry Hours`),
      });
  }

  updateStatus(golfIndustryHoursStatusId: number, reasonForDenial?: string) {
    const golfIndustryHoursGuid = this.golfIndustryHours()?.publicId;
    if (!golfIndustryHoursGuid) return;
    this.golfIndustryHoursRepository
      .unauthenticatedUpdateGolfIndustryHours(golfIndustryHoursGuid, {
        golfIndustryHoursStatusId,
        reasonForDenial,
      })
      .pipe(indicateSignal(this.isStatusUpdateSubmitting))
      .subscribe({
        next: golfIndustryHours => {
          if (!golfIndustryHours) return;
          this.golfIndustryHours.set(golfIndustryHours);
        },
        error: e => this.errorService.handle(e, $localize`Error Updating Golf Industry Hours Status`),
      });
  }
}
